import React, { useState } from 'react';
import { Button, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { useGridApiContext } from '@mui/x-data-grid';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useTranslation } from 'react-i18next';

import exportIcon from '../../../../assets/img/exportIcon.svg';
import { TotalItemsLabel } from '@/components';
import { TableHeaderLayout } from '@/layouts';
import palette from '@/theme/palette.ts';

import { styles } from './styles.ts';

interface OrderTableHeaderProps {
  count: number;
  selectedRows: number[];
  rows: number[];
  onDelete: () => void;
  onCancel: () => void;
  isExportDisabled?: boolean;
  onExport?: () => void;
  onExportXlsx?: () => void;
}

export const OrderTableHeader = ({
  count,
  selectedRows,
  rows,
  onDelete,
  onCancel,
  isExportDisabled = false,
  onExport,
  onExportXlsx,
}: OrderTableHeaderProps) => {
  const { t } = useTranslation();
  const apiRef = useGridApiContext();
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);

  const handleExport = () => {
    if (onExport) {
      onExport();
      return;
    }
    if (selectedRows.length > 0) {
      apiRef.current.exportDataAsCsv({
        getRowsToExport: () => rows.filter((row) => selectedRows.includes(row)),
      });
    } else {
      apiRef.current.exportDataAsCsv();
    }
  };

  const handleExportButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    if (onExportXlsx) {
      setExportMenuAnchor(event.currentTarget);
      return;
    }
    handleExport();
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportPdfClick = () => {
    handleExportMenuClose();
    handleExport();
  };

  const handleExportXlsxClick = () => {
    handleExportMenuClose();
    onExportXlsx?.();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TableHeaderLayout
        selectedRows={selectedRows}
        onDeactivate={() => {}}
        onActivate={() => {}}
        onDuplicate={() => {}}
        onCancel={onCancel}
        onDelete={onDelete}
        actions={{ onDelete: true, onDuplicate: false, onDeactivate: false, onActivate: false }}
      >
        <Stack direction="row" justifyContent="start" alignItems="center" gap={2}>
          <TotalItemsLabel count={count} />
          {!isExportDisabled && (
            <>
              <Button sx={styles.exportButton} onClick={handleExportButtonClick}>
                <img src={exportIcon} alt="exportIcon" />
                <Typography variant="subtitle2" sx={{ color: palette.text.primaryColor }}>
                  {t('GLOBAL.EXPORT').toUpperCase()}
                </Typography>
              </Button>
              {onExportXlsx && (
                <Menu
                  anchorEl={exportMenuAnchor}
                  open={Boolean(exportMenuAnchor)}
                  onClose={handleExportMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  <MenuItem onClick={handleExportPdfClick}>{t('GLOBAL.EXPORT_PDF')}</MenuItem>
                  <MenuItem onClick={handleExportXlsxClick}>{t('GLOBAL.EXPORT_EXCEL')}</MenuItem>
                </Menu>
              )}
            </>
          )}
        </Stack>
      </TableHeaderLayout>
    </LocalizationProvider>
  );
};
